import React, {useCallback, useEffect, useState} from "react";
import Api from "../../Api";
import {withAuth} from "../../hoc/Auth";
import {Column, Container, Wrapper} from "../../hoc/Layout";
import {Link, useHistory, useParams} from "react-router-dom";
import PayoutsTable from "../../Components/PayoutsTable";
import Search from "../../Components/Utils/SearchString";
import PayoutsDetailFilters from "../../Components/PayoutsDetailFilters";
import PayoutsDetailEditing from "../../Components/PayoutsDetailEditing";
import PayoutsDetailAmounts from "../../Components/PayoutsDetailAmounts";
import ReportWidget from "../../Components/Utils/ReportWidget";
import Modal from "../../Components/Utils/Modal";
import FilterPanel from "../../Components/Utils/FilterPanel";
import Icon from "../../Components/Utils/Icon";
import {FormattedMessage} from "react-intl";

import classes from "./styles.module.css";

const PayoutsDetail = withAuth(({token}) => {

    const {id} = useParams();
    const history = useHistory();

    const excludeColumns = ["date", "type", "accountingDate", "status", "currencyCode", "cardBrand", "cardBankName", "cardBankCountryCode"];

    const [dataList, setDataList] = useState([]);
    const [filteredDataList, setFilteredDataList] = useState([]);
    const [isOpenModal, setIsOpenModal] = useState(false);

    const [info, setInfo] = useState({
        name: "", tags: [], successAmount: 0, failedAmount: 0, otherAmount: 0, currencyCode: "RUB", merchantUuid: null
    });

    const [suggestionsTags, setSuggestionsTags] = useState([]);

    const [filters, setFilters] = useState({
        payout: false, payoutDecline: false, created: false, filtered: false,
    });

    const [searchString, setSearchString] = useState("");
    const [loading, setLoading] = useState(false);

    // Состояние для модального окна предупреждения о pending транзакциях
    const [pendingWarningModal, setPendingWarningModal] = useState(false);
    const [pendingTransactions, setPendingTransactions] = useState([]);

    const matchItem = useCallback((item, lower) => {
        return Object.keys(item).some(key => {
            if (excludeColumns.includes(key)) return false;

            const val = item[key];

            if (val === null || val === undefined) return false;
            if (typeof val !== "string" && typeof val !== "number") return false;

            return val.toString().toLowerCase().includes(lower);
        });
    }, []);

    const filterData = useCallback((value) => {

        const lower = value.toLowerCase().trim();

        const anyFiltersActive = Object.values(filters).some(Boolean);

        if (!lower && !anyFiltersActive) {
            setFilteredDataList(dataList);
            return;
        }

        const result = dataList.filter(item => {
            if (anyFiltersActive) {
                if (filters.created && item.type === "created") return matchItem(item, lower);
                if (filters.payoutDecline && item.type === "payoutDecline") return matchItem(item, lower);
                if (filters.payout && item.type === "payout") return matchItem(item, lower);
                if (filters.filtered && item.type === "filtered") return matchItem(item, lower);

                return false;
            }

            return matchItem(item, lower);
        });

        setFilteredDataList(result);
    }, [dataList, filters, matchItem]);

    const onChangeSearch = value => {
        setSearchString(value);
        filterData(value);
    };

    useEffect(() => {

        const api = new Api({token});

        api.setPayoutsList(id)
            .then(r => {

                if (!r) return;

                setDataList(r.data || []);
                setFilteredDataList(r.data || []);

                setInfo({
                    name: r.info && r.info.name ? r.info.name : "",
                    merchantUuid: r.info && r.info.merchantUuid ? r.info.merchantUuid : null,
                    tags: r.info && r.info.tags ? r.info.tags : [],
                    successAmount: r.info && r.info.successAmount ? r.info.successAmount : 0,
                    failedAmount: r.info && r.info.failedAmount ? r.info.failedAmount : 0,
                    otherAmount: r.info && r.info.otherAmount ? r.info.otherAmount : 0,
                    currencyCode: r.info && r.info.currencyCode ? r.info.currencyCode : "RUB",
                });

                setLoading(true);
            })
            .catch(err => {
            });

    }, [id, token]);

    useEffect(() => {

        if (!info.merchantUuid) {
            return;
        }

        new Api({token}).getTags(info.merchantUuid)
            .then(r => {
                setSuggestionsTags(r || []);
            });

    }, [info.merchantUuid, token]);

    // Получить неуспешные транзакции (payoutDecline)
    const getFailedTransactions = useCallback(() => {
        return dataList.filter(item => item.type === "payoutDecline");
    }, [dataList]);

    // Получить pending транзакции (created)
    const getPendingTransactions = useCallback(() => {
        return dataList.filter(item => item.type === "created");
    }, [dataList]);

    // Форматировать PAN для отображения
    const formatPan = (item) => {
        return `${item.firstPart || '****'} •••• ${item.lastPart || '****'}`;
    };

    // Преобразовать неуспешные транзакции в формат реестра
    const convertToRegistryFormat = useCallback((transactions) => {
        return transactions.map((item, index) => ({
            row: {
                pan: item.firstPart && item.lastPart ? `${item.firstPart}******${item.lastPart}` : '',
                amount: item.amount || 0,
                recipient: item.recipient || '',
                phone: item.phone || '',
            },
            hasError: false,
            errors: null,
            index: index
        }));
    }, []);

    // Навигация на страницу загрузки реестра
    const navigateToRegistryUpload = useCallback((failedTransactions) => {
        const registryData = convertToRegistryFormat(failedTransactions);
        const registryName = `Повтор_${info.name || 'реестра'}_${new Date().toLocaleDateString('ru-RU')}`;

        history.push('/registry-upload', {
            preloadedRegistry: registryData,
            preloadedName: registryName,
            sourceRegistryId: id
        });
    }, [convertToRegistryFormat, info.name, history, id]);

    // Обработчик клика на кнопку "Повторить неуспешные"
    const handleRetryFailed = useCallback(() => {
        const failed = getFailedTransactions();

        if (failed.length === 0) {
            return;
        }

        const pending = getPendingTransactions();

        if (pending.length > 0) {
            // Проверяем совпадения по карте и сумме
            const matchingPending = pending.filter(pendingItem => {
                return failed.some(failedItem =>
                    pendingItem.firstPart === failedItem.firstPart &&
                    pendingItem.lastPart === failedItem.lastPart &&
                    pendingItem.amount === failedItem.amount
                );
            });

            if (matchingPending.length > 0) {
                setPendingTransactions(matchingPending);
                setPendingWarningModal(true);
                return;
            }
        }

        // Нет pending или нет совпадений - переходим сразу
        navigateToRegistryUpload(failed);
    }, [getFailedTransactions, getPendingTransactions, navigateToRegistryUpload]);

    // Подтверждение в модальном окне - продолжить несмотря на pending
    const handleConfirmRetry = useCallback(() => {
        setPendingWarningModal(false);
        const failed = getFailedTransactions();
        navigateToRegistryUpload(failed);
    }, [getFailedTransactions, navigateToRegistryUpload]);

    // Количество неуспешных транзакций
    const failedCount = dataList.filter(item => item.type === "payoutDecline").length;

    const pageContent = (
        <Wrapper>
            <Container style={{justifyContent: 'center'}}>
                <Column width={1180}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Link to={"/payouts"}>
                            <button type="button" className="btn btn-outline-primary">Назад</button>
                        </Link>
                        <h4>Реестр: {info.name}</h4>
                        <button className="btn btn-outline-primary" onClick={() => setIsOpenModal(true)}>Фильтры и доп.
                            действия
                        </button>
                    </div>
                    <PayoutsDetailAmounts
                        success={info.successAmount}
                        failed={info.failedAmount}
                        other={info.otherAmount}
                        currency={info.currencyCode}
                    />

                    <Search
                        searchString={searchString}
                        onChangeSearch={onChangeSearch}
                        isDisabled={false}
                        placeholder={"Search.label.registryData"}
                    />

                    <div className={classes.Card}>
                        <PayoutsTable
                            payouts={filteredDataList}
                            loading={loading}
                        />
                    </div>
                </Column>
            </Container>

            {/* Модальное окно предупреждения о pending транзакциях */}
            <Modal
                isOpen={pendingWarningModal}
                onClose={() => setPendingWarningModal(false)}
                onConfirm={handleConfirmRetry}
                title={<FormattedMessage id="PayoutsDetail.modal.warningTitle" defaultMessage="Внимание"/>}
                isConfirm={true}
            >
                <div className={classes.WarningContent}>
                    <div className={classes.WarningIcon}>
                        <Icon icon="warning" size="48px" color="#ff9800"/>
                    </div>
                    <p className={classes.WarningText}>
                        <FormattedMessage
                            id="PayoutsDetail.modal.pendingWarning"
                            defaultMessage="В обработке уже находятся карты из загруженного реестра. Возможно дублирование выплат."
                        />
                    </p>

                    {pendingTransactions.length > 0 && (
                        <div className={classes.PendingList}>
                            <p className={classes.PendingListTitle}>
                                <FormattedMessage
                                    id="PayoutsDetail.modal.pendingListTitle"
                                    defaultMessage="Совпадающие транзакции в обработке:"
                                />
                            </p>
                            <div className={classes.PendingTableWrap}>
                                <table className={classes.PendingTable}>
                                    <thead>
                                    <tr>
                                        <th><FormattedMessage id="PayoutsDetail.modal.card" defaultMessage="Карта"/></th>
                                        <th><FormattedMessage id="PayoutsDetail.modal.amount" defaultMessage="Сумма"/></th>
                                        <th><FormattedMessage id="PayoutsDetail.modal.recipient"
                                                              defaultMessage="Получатель"/></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {pendingTransactions.slice(0, 5).map((item, idx) => (
                                        <tr key={idx}>
                                            <td>{formatPan(item)}</td>
                                            <td>{item.amount} {item.currencyCode || info.currencyCode}</td>
                                            <td>{item.recipient || '—'}</td>
                                        </tr>
                                    ))}
                                    {pendingTransactions.length > 5 && (
                                        <tr>
                                            <td colSpan={3} className={classes.MoreItems}>
                                                <FormattedMessage
                                                    id="PayoutsDetail.modal.moreItems"
                                                    defaultMessage="... и ещё {count}"
                                                    values={{count: pendingTransactions.length - 5}}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        </Wrapper>
    );

    return (
        <FilterPanel title="Фильтры и доп. действия" isOpen={isOpenModal} onClose={() => setIsOpenModal(false)} content={pageContent}>
            <PayoutsDetailFilters
                filters={filters}
                isLoading={loading}
                onSelectFilter={(updater) => {
                    setFilters(updater);
                }}
                onApplyFilters={() => {
                    filterData(searchString);
                }}
            />

            <PayoutsDetailEditing
                token={token}
                registryId={id}
                name={info.name}
                tags={info.tags}
                suggestionsTags={suggestionsTags}
            />

            <ReportWidget
                access={true}
                token={token}
                type="singleRegistry"
                filter={Number(id)}
                count={dataList.length}
                countLabel="Платежей"
                name={info.name}
                date={false}
            />

            {/* Кнопка повторить неуспешные */}
            {failedCount > 0 && (
                <div className={classes.RetryCard}>
                    <button
                        className={classes.RetryBtn}
                        onClick={handleRetryFailed}
                    >
                        <Icon className={classes.RetryIcon} icon="refresh" size="18px"/>
                        <FormattedMessage
                            id="PayoutsDetail.btn.retryFailed"
                            defaultMessage="Повторить неуспешные"
                        />
                        <span className={classes.RetryCount}>({failedCount})</span>
                    </button>
                </div>
            )}
        </FilterPanel>
    );
});

export default PayoutsDetail;



// WEBPACK FOOTER //
// ./src/PagesOfUser/PayoutsDetail/index.js